import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Leaf, ArrowLeft, TreePine } from "lucide-react";

export default function FamilyTree() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/tree")
      .then((r) => setNodes(r.data))
      .finally(() => setLoading(false));
  }, []);

  // Build parent → children (using father_family_id or mother_family_id)
  const { roots, childrenOf, byId } = useMemo(() => {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const childrenOf = new Map();
    for (const n of nodes) {
      const parents = [n.father_family_id, n.mother_family_id].filter(Boolean);
      for (const p of parents) {
        if (!byId.has(p)) continue;
        if (!childrenOf.has(p)) childrenOf.set(p, []);
        // avoid duplicates when both parents link
        if (!childrenOf.get(p).find((c) => c.id === n.id)) {
          childrenOf.get(p).push(n);
        }
      }
    }
    const roots = nodes.filter(
      (n) => !(n.father_family_id && byId.has(n.father_family_id)) &&
             !(n.mother_family_id && byId.has(n.mother_family_id))
    );
    return { roots, childrenOf, byId };
  }, [nodes]);

  const renderNode = (n, depth = 0, seen = new Set()) => {
    if (seen.has(n.id)) return null; // cycle guard
    seen.add(n.id);
    const kids = childrenOf.get(n.id) || [];
    return (
      <div
        key={n.id}
        className="relative"
        data-testid={`tree-node-${n.id}`}
      >
        <div className="bg-white border border-[#e6e2d6] rounded-2xl px-5 py-4 inline-block card-lift">
          <div className="font-serif text-lg text-stone-900">
            {n.name || "Unnamed"}
          </div>
          {n.birth_date && (
            <div className="text-xs text-stone-500">b. {n.birth_date}</div>
          )}
          {(n.father_name || n.mother_name) && (
            <div className="text-xs text-stone-500 mt-1">
              {[n.father_name, n.mother_name].filter(Boolean).join(" · ")}
            </div>
          )}
          {n.children?.length > 0 && (
            <div className="text-xs text-stone-500 mt-1">
              {n.children.length} child{n.children.length === 1 ? "" : "ren"} on file
            </div>
          )}
        </div>
        {kids.length > 0 && (
          <div className="pl-8 mt-4 border-l-2 border-[#eae5d9] ml-4">
            <div className="space-y-4">
              {kids.map((k) => (
                <div key={k.id} className="pl-4">
                  {renderNode(k, depth + 1, new Set(seen))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="border-b border-[#e6e2d6] bg-white/70 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2">
            <img src="/bailey-logo.png" alt="Bailey Family Reunion" className="h-9 w-9 rounded-full object-cover ring-2 ring-[#1a2947]/10" />
            <span className="font-serif text-xl text-stone-900">Bailey Family Reunion</span>
            <span className="label-eyebrow text-stone-400 ml-2">Tree</span>
          </Link>
          <Link
            to="/admin"
            className="label-eyebrow text-stone-500 hover:text-[#1a2947] flex items-center gap-1"
            data-testid="tree-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Families
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <TreePine className="h-3 w-3" /> Lineage
          </div>
          <h1 className="font-serif text-4xl md:text-5xl text-stone-900 tracking-tight">
            The Family Tree
          </h1>
          <p className="text-stone-600 mt-3 max-w-xl">
            Link a family to their parents from the edit dialog to shape this tree.
            Families with no linked parents show as roots.
          </p>
        </div>

        {loading ? (
          <div className="text-stone-500">Loading...</div>
        ) : nodes.length === 0 ? (
          <div className="bg-white border border-[#e6e2d6] rounded-2xl p-14 text-center text-stone-500">
            No families yet.
          </div>
        ) : (
          <div className="space-y-8">
            {roots.length === 0 ? (
              <div className="text-stone-500">
                No linked parents yet — showing all as roots.
              </div>
            ) : null}
            {(roots.length ? roots : nodes).map((n) => (
              <div key={n.id}>{renderNode(n)}</div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
